import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CustomerHeader.css';
import { supabase } from '../config/supabase';
import { getRestaurantInfo } from '../utils/restaurantInfo';

const CustomerHeader = ({ searchQuery, onSearchChange, cartItemCount, onCartClick }) => {
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [restaurantLogo, setRestaurantLogo] = useState(null);
  const [restaurantName, setRestaurantName] = useState('Restaurant');

  // Check authentication from localStorage
  useEffect(() => {
    const checkAuth = () => {
      const customerData = localStorage.getItem('customer');
      if (customerData) {
        try {
          const customer = JSON.parse(customerData);
          setIsLoggedIn(true);
          setUser({
            email: customer.email,
            name: customer.name,
            phone: customer.phone
          });
        } catch (err) {
          console.error('Error parsing customer data:', err);
          localStorage.removeItem('customer');
        }
      }
    };
    checkAuth();
  }, []);

  // Fetch restaurant info (logo and name)
  useEffect(() => {
    const loadRestaurantInfo = async () => {
      try {
        const { data, error } = await getRestaurantInfo();
        if (!error && data) {
          if (data.logo_url) {
            setRestaurantLogo(data.logo_url);
          }
          if (data.name) {
            setRestaurantName(data.name);
          }
        }
      } catch (error) {
        console.error('Error loading restaurant info:', error);
      }
    };
    loadRestaurantInfo();
  }, []);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  const handleLoginClick = () => {
    setIsDrawerOpen(false);
    navigate('/login');
  };

  return (
    <>
      {/* Top Navigation Bar */}
      <header className="top-navbar">
        <div className="nav-container">
          {/* LEFT - Logo & Name */}
          <div className="nav-left" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            {restaurantLogo ? (
              <img 
                src={restaurantLogo} 
                alt={restaurantName}
                className="header-logo"
              />
            ) : (
              <span className="restaurant-name">{restaurantName}</span>
            )}
          </div>

          {/* MIDDLE - Search (Desktop Only) */}
          <div className="nav-middle">
            <div className="search-bar-container">
              <div className="search-icon">🔍</div>
              <input
                type="text"
                className="search-input"
                placeholder="Search for dishes..."
                value={searchQuery || ''}
                onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
              />
            </div>
          </div>

          {/* RIGHT - Hamburger Menu (Desktop & Mobile) */}
          <button
            className="hamburger-menu"
            onClick={() => setIsDrawerOpen(true)}
            aria-label="Open menu"
          >
            ☰
          </button>
        </div>
      </header>

      {/* Mobile Side Drawer */}
      <div
        className={`drawer-overlay ${isDrawerOpen ? 'active' : ''}`}
        onClick={() => setIsDrawerOpen(false)}
      />
      <div className={`mobile-drawer ${isDrawerOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          {isLoggedIn && user ? (
            <>
              <div className="user-avatar">
                {user.name?.charAt(0).toUpperCase()}
              </div>
              <div className="user-info">
                <div className="user-name">{user.name}</div>
              </div>
            </>
          ) : null}
          <button
            className="drawer-close"
            onClick={() => setIsDrawerOpen(false)}
            aria-label="Close menu"
          >
            ×
          </button>
        </div>
        <div className="drawer-menu-items">
          <div className="drawer-menu-item" onClick={() => {
            setIsDrawerOpen(false);
            navigate('/');
          }}>
            <span className="menu-item-text">Home</span>
          </div>
          <div className="drawer-menu-item" onClick={() => {
            setIsDrawerOpen(false);
            navigate('/menu');
          }}>
            <span className="menu-item-text">Menu</span>
          </div>
          {isLoggedIn && (
            <>
              <div className="drawer-menu-item" onClick={() => {
                setIsDrawerOpen(false);
                navigate('/orders');
              }}>
                <span className="menu-item-text">My Orders</span>
              </div>
              <div className="drawer-menu-item" onClick={() => {
                setIsDrawerOpen(false);
                navigate('/profile');
              }}>
                <span className="menu-item-text">Profile</span>
              </div>
            </>
          )}
          {!isLoggedIn && (
            <div className="drawer-menu-item" onClick={handleLoginClick}>
              <span className="menu-item-text">Login</span>
            </div>
          )}
          <div className="drawer-menu-item" onClick={() => {
            setIsDrawerOpen(false);
            navigate('/contact');
          }}>
            <span className="menu-item-text">Contact Us</span>
          </div>
          {isLoggedIn && (
            <div className="drawer-menu-item" onClick={() => { 
              setIsLoggedIn(false); 
              setUser(null); 
              localStorage.removeItem('customer');
              setIsDrawerOpen(false);
              window.location.href = '/';
            }}>
              <span className="menu-item-text">Logout</span>
            </div>
          )}
        </div>
      </div>

      {/* Floating Cart Button (Desktop & Mobile) */}
      <button
        className="header-floating-cart-button"
        onClick={onCartClick || (() => navigate('/cart'))}
        aria-label="Open cart"
      >
        <span className="header-floating-cart-icon">🛒</span>
        {cartItemCount > 0 && (
          <span className="header-floating-cart-badge">{cartItemCount}</span>
        )}
      </button>
    </>
  );
};

export default CustomerHeader;

